// components/OfferColumns.tsx
import React from "react";
import { formatCurrencyUSD } from "utils/CommonUtils";
import { formatServerDate } from "utils/CalanderUtils";
import EditCellModal from "../EditableCellModal";
import {
  Autocomplete,
  Box,
  FormControl,
  Icon,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { ArrowDownward, Edit, KeyboardArrowDown } from "@mui/icons-material";
import MDBox from "components/MDBox";
import GenericEmailSenderModal from "pages/applications/components/GenericEmailSenderModel";
import { get } from "requests";

const TruncatedCell = ({ value }: { value: any }) => (
  <div
    style={{
      fontFamily: "Roboto",
      fontWeight: 400,
      fontStyle: "normal",
      fontSize: "12px",
      lineHeight: "140%",
      letterSpacing: "0px",
      verticalAlign: "middle",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    }}
    title={value}
  >
    {value == null || value === undefined ? "" : value}
  </div>
);

export const OFFER_TABLE_HEADER = {
  MERCHANT: "MERCHANT",
  OFFER_AMOUNT: "OFFER AMOUNT",
  APPLICATION_NO: "APPLICATION #",
  FACTOR_RATE: "FACTOR RATE",
  TERM: "TERM (# of Payments)",
  BROKER: "BROKER/ISO",
  SALES_REP: "SALES REP",
  INTERNAL_REP: "INTERNAL REP",
  COUNTER_OFFER: "COUNTER OFFER",
  REBUTTAL: "REBUTTAL",
  OFFER_STATUS: "OFFER STATUS",
  APP_STATUS: "APPLICATION STATUS",
  NOTES: "OFFER NOTES",
  DATE_SENT: "DATE SENT",
};

const OfferColumns = (
  onEditCellChange: (
    rowData: any,
    columnId: string,
    newValue: string,
    sendEmail: boolean,
    emailContent: any,
    replyOnSameThread: boolean,
    recepients : any,
  ) => Promise<void>
) => [
  {
    Header: OFFER_TABLE_HEADER.DATE_SENT,
    accessor: "dateSent",
    width: 70,
    Cell: ({ value }: any) => <TruncatedCell value={formatServerDate(value)} />,
  },
  {
    Header: OFFER_TABLE_HEADER.APPLICATION_NO,
    accessor: "applicationNo",
    width: 100,
    Cell: ({ value }: any) => <TruncatedCell value={value} />,
  },
  {
    Header: OFFER_TABLE_HEADER.MERCHANT,
    accessor: "merchantName",
    width: 120,
    Cell: ({ value }: any) => <TruncatedCell value={value} />,
  },
  {
    Header: OFFER_TABLE_HEADER.OFFER_AMOUNT,
    accessor: "offerAmount",
    width: 110,
    Cell: ({ value }: any) => <TruncatedCell value={formatCurrencyUSD(value)} />,
  },
  {
    Header: OFFER_TABLE_HEADER.FACTOR_RATE,
    accessor: "factorRate",
    width: 100,
    Cell: ({ value }: any) => <TruncatedCell value={value?.toFixed(3)} />,
  },
  {
    Header: OFFER_TABLE_HEADER.TERM,
    accessor: "term",
    width: 120,
    Cell: ({ value }: any) => <TruncatedCell value={value} />,
  },
  {
    Header: OFFER_TABLE_HEADER.BROKER,
    accessor: "brokerIso",
    width: 120,
    Cell: ({ value }: any) => <TruncatedCell value={value} />,
  },
  {
    Header: OFFER_TABLE_HEADER.SALES_REP,
    accessor: "salesRep",
    width: 130,
    Cell: ({ value }: any) => <TruncatedCell value={value} />,
  },
  {
    Header: OFFER_TABLE_HEADER.INTERNAL_REP,
    accessor: "internalIsoRep",
    width: 120,
    Cell: ({ value }: any) => <TruncatedCell value={value} />,
  },
  {
    Header: OFFER_TABLE_HEADER.REBUTTAL,
    accessor: "rebuttal",
    width: 120,
    Cell: ({ value, row, column }: any) => {
      const [open, setOpen] = React.useState(false);
      const [tempValue, setTempValue] = React.useState(value);

      const offerStatus = row.original.offerStatus;
      const isDisabled =
        !offerStatus || ["declined", "accepted"].includes(offerStatus.toLowerCase());

      React.useEffect(() => {
        setTempValue(value);
      }, [value]);

      const handleSave = async (newValue: string) => {
        await onEditCellChange(row.original, column.id, newValue, false, null,false,[]);
        setOpen(false);
      };

      return (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            sx={{
              cursor: "pointer",
              maxWidth: "100%",
              textAlign: "right",
            }}
          >
            <TruncatedCell value={value} />

            { !isDisabled &&
            <Edit
              onClick={() => {
                if (!isDisabled) setOpen(true);
              }}
              sx={{
                ml: 1,
                fontSize: 18,
                color: isDisabled ? "#ccc" : "#1976d2",
                flexShrink: 0,
                pointerEvents: isDisabled ? "none" : "auto",
                ":hover": {
                  color: isDisabled ? "#ccc" : "#1565c0",
                },
              }}
            />
            }
          </Box>

          <EditCellModal
            open={open}
            onClose={() => setOpen(false)}
            onSave={handleSave}
            value={tempValue || ""}
            title="Edit Rebuttal"
            multiline={true}
          />
        </>
      );
    },
  },
  {
    Header: OFFER_TABLE_HEADER.COUNTER_OFFER,
    accessor: "counterOffer",
    width: 120,
    Cell: ({ value, row, column }: any) => {
      const [open, setOpen] = React.useState(false);
      
      const dealAppId= row.original.dealApplicationId;
      const offerKey =row.original.offerKey;
      
      const offerStatus = row.original.offerStatus;
      const isDisabled =
        !offerStatus || ["declined", "accepted"].includes(offerStatus.toLowerCase());
       const [isAutoSubmitted, setIsAutoSubmitted] = React.useState(false);

       const getIsAutoSubmitted = async ()=>{
        try{
          const r = await get("dealApplication/isAutoSubmitted/" +dealAppId );
          setIsAutoSubmitted(r);
        }catch{
            setIsAutoSubmitted(false)
        }
        
       }

      const handleSave = async (newValue: string, emailContent: any, sendEmail: boolean, replyOnSameThread: boolean, recipients:  any) => {
        await onEditCellChange(row.original, column.id, newValue, sendEmail,emailContent,replyOnSameThread, recipients);
        setOpen(false);
      };

      return (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            sx={{
              cursor: "pointer",
              maxWidth: "100%",
              textAlign: "right",
            }}
          >
            <TruncatedCell value={value} />

         { !isDisabled &&
            <Edit
              onClick={async () => {
                await getIsAutoSubmitted();
                if (!isDisabled) setOpen(true);
              }}
              sx={{
                ml: 1,
                fontSize: 18,
                color: isDisabled ? "#ccc" : "#1976d2",
                flexShrink: 0,
                pointerEvents: isDisabled ? "none" : "auto",
                ":hover": {
                  color: isDisabled ? "#ccc" : "#1565c0",
                },
              }}
            />
           }

      </Box>

      {open && (
        <GenericEmailSenderModal
              selected={[offerKey]}
              header={"Send Counter Offer"}
              dealApplicationId={dealAppId}
              topicName="event.deal.dealApplication.offers.counter"
              showModal={open}
              onClose={() => setOpen(false)}
              refresh={() => console.log("refreshing")}
              onSend={handleSave}
              isAutoSubmitted={isAutoSubmitted}
              customTextProps={value}        />
        )}
        </>
      );
    },
  },
  {
    Header: OFFER_TABLE_HEADER.OFFER_STATUS,
    accessor: "offerStatus",
    width: 160,
    Cell: ({ value, row, column }: any) => {
      const [editing, setEditing] = React.useState(false);
      const [selectedStatus, setSelectedStatus] = React.useState(value);

      const STATUS_OPTIONS = [
        "Not Sent",
        "Accepted",
        "Sent",
        "Declined",
        "Timed Out",
        "Pending Response",
        "Response Sent",
      ];

      const handleSave = () => {
        onEditCellChange(row.original, column.id, selectedStatus, false, null,false,[]);
        setEditing(false);
      };

      return (
        <MDBox display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
          {editing ? (
            <Autocomplete
              options={STATUS_OPTIONS}
              value={selectedStatus}
              onChange={(event, newValue) => {
                setSelectedStatus(newValue);
              }}
              onBlur={handleSave}
              disableClearable
              autoHighlight
              autoFocus
              popupIcon={<KeyboardArrowDown />}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  sx={{
                    minWidth: 120,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "primary.main",
                      },
                      "&:hover fieldset": {
                        borderColor: "primary.dark",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              )}
            />
          ) : (
            <>
              <TruncatedCell value={value} />
              <Tooltip title="Edit Status">
                <IconButton size="small" onClick={() => setEditing(true)}>
                  <Icon sx={{ fontSize: 16, color: "#1976d2" }}>edit</Icon>
                </IconButton>
              </Tooltip>
            </>
          )}
        </MDBox>
      );
    },
  },
  {
    Header: OFFER_TABLE_HEADER.APP_STATUS,
    accessor: "appStatus",
    width: 130,
    Cell: ({ value }: any) => <TruncatedCell value={value} />,
  },
  {
    Header: OFFER_TABLE_HEADER.NOTES,
    accessor: "offerNotes",
    width: 200,
    Cell: ({ value, row, column }: any) => {
      const [open, setOpen] = React.useState(false);
      const [tempValue, setTempValue] = React.useState(value);

      React.useEffect(() => {
        setTempValue(value);
      }, [value]);

      const handleSave = async (newValue: string) => {
        await onEditCellChange(row.original, column.id, newValue, false, null,false,[]);
        setOpen(false);
      };

      return (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ cursor: "pointer", maxWidth: "100%" }}
          >
            <TruncatedCell value={value} />

            <Edit
              onClick={() => setOpen(true)}
              sx={{
                ml: 1,
                fontSize: 18,
                color: "#1976d2",
                flexShrink: 0,
                ":hover": { color: "#1565c0" },
              }}
            />
        
          </Box>

          <EditCellModal
            open={open}
            onClose={() => setOpen(false)}
            onSave={handleSave}
            value={tempValue || ""}
            title="Edit Offer Notes"
            multiline={true}
          />
        </>
      );
    },
  },
];

export default OfferColumns;
